import { useCallback, useEffect, useState } from 'react';

import { FilePickerProps } from './file-picker.model';

const useFilePicker = <T extends boolean>({
  value,
  allowedExtensions = [],
  multiple,
  onChange,
  onError,
}: FilePickerProps<T>) => {
  // Helper function to convert value prop to files array
  const convertValueToFilesArray = useCallback(
    (inputValue: File | File[] | null | undefined): File[] => {
      if (Array.isArray(inputValue)) {
        return inputValue;
      } else if (inputValue) {
        return [inputValue];
      }
      return [];
    },
    []
  );

  // Helper function to convert files array to appropriate value type for onChange
  const convertFilesArrayToValue = useCallback(
    (files: File[]): T extends true ? File[] : File => {
      return (multiple ? files : files[0]) as T extends true ? File[] : File;
    },
    [multiple]
  );

  const [selectedFiles, setSelectedFiles] = useState<File[]>(() =>
    convertValueToFilesArray(value)
  );

  // Reset state when value prop changes
  useEffect(() => {
    setSelectedFiles(convertValueToFilesArray(value));
  }, [value, convertValueToFilesArray]);

  const isFileExtensionAllowed = useCallback(
    (file: File): boolean => {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension) return false;
      return (
        allowedExtensions.length === 0 ||
        allowedExtensions.includes(`.${fileExtension}`)
      );
    },
    [allowedExtensions]
  );

  const separateValidAndInvalidFiles = useCallback(
    (filesToValidate: File[]): { validFiles: File[]; invalidFiles: File[] } => {
      const validFiles: File[] = [];
      const invalidFiles: File[] = [];

      for (const file of filesToValidate) {
        if (isFileExtensionAllowed(file)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file);
        }
      }

      return { validFiles, invalidFiles };
    },
    [isFileExtensionAllowed]
  );

  const processSelectedFiles = useCallback(
    (fileList: FileList): void => {
      const { validFiles, invalidFiles } = separateValidAndInvalidFiles(
        Array.from(fileList)
      );

      const updatedFiles = multiple
        ? [...selectedFiles, ...validFiles]
        : validFiles;
      setSelectedFiles(updatedFiles);

      if (updatedFiles.length > 0) {
        onChange(convertFilesArrayToValue(updatedFiles));
      }

      if (invalidFiles.length > 0) {
        const errorMessage = `Invalid file type. Only ${allowedExtensions.join(', ')} files are allowed.`;
        onError?.(errorMessage);
      }
    },
    [
      selectedFiles,
      multiple,
      separateValidAndInvalidFiles,
      onChange,
      convertFilesArrayToValue,
      allowedExtensions,
      onError,
    ]
  );

  const removeSelectedFile = useCallback(
    (fileToRemove: File): void => {
      const remainingFiles = selectedFiles.filter(
        (file) => file.name !== fileToRemove.name
      );
      setSelectedFiles(remainingFiles);
      onChange(convertFilesArrayToValue(remainingFiles));
    },
    [selectedFiles, onChange, convertFilesArrayToValue]
  );

  return {
    files: selectedFiles,
    handleFiles: processSelectedFiles,
    removeFile: removeSelectedFile,
  };
};

export default useFilePicker;
